/**
 * @theme/cart — every write to the cart goes through this module
 * (DESIGN-PLAN 7.G2; it replaces cba-cart-events.js, which wrapped
 * window.fetch and XMLHttpRequest to notice cart changes).
 *
 *   import { addToCart, changeLine, updateCart, discountCodeProblem, openCart, loadSection } from '@theme/cart';
 *
 * Shopify's standard storefront events
 * (shopify.dev/docs/api/storefront-events-and-actions): each write
 * dispatches its event as it starts, from the element the shopper used, and
 * the event's promise settles when the cart has answered.
 * - shopify:cart:lines-update for adds, quantity changes and removals;
 * - shopify:cart:discount-update for discount codes;
 * - shopify:cart:error when the request fails. When the cart declines (sold
 *   out, not enough stock), the promise resolves with userErrors instead, and
 *   the function rejects with a CartDeclinedError carrying Shopify's reason.
 * The theme's own parts (the header count, the reward bars, the cart)
 * listen to the same events, so a change an app makes through
 * Shopify.actions.updateCart updates them too.
 *
 * Each request asks for the sections that show the cart (every
 * [data-cba-cart-section] on the page, at most five; Section Rendering API),
 * so those parts re-render from the answer instead of fetching again. The
 * answer's sections are in the event result's detail.sections.
 */
import { CartLinesUpdateEvent, CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/standard-events';

/** The cart declined the change; the message is Shopify's reason. */
export class CartDeclinedError extends Error {}

/** The ids of the sections that show the cart, for the Section Rendering API. */
export function cartSectionIds() {
  const ids = Array.from(document.querySelectorAll('[data-cba-cart-section]'), (el) => el.dataset.cbaCartSection);
  return Array.from(new Set(ids.filter(Boolean))).slice(0, 5);
}

export function getCart() {
  return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } }).then((r) => {
    if (!r.ok) throw new Error('cart ' + r.status);
    return r.json();
  });
}

async function post(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: cartSectionIds(), sections_url: window.location.pathname })
  });
  const json = await response.json();
  return { ok: response.ok, json };
}

/* Runs one request and settles the events' promises. `request` resolves
   with { ok, json }; add.js answers with the added items rather than the
   cart, so the cart is read after it. A declined change resolves with the
   cart as it stands and userErrors; a failed request rejects and dispatches
   shopify:cart:error. Every path settles every promise, or listeners would
   wait for ever. */
async function run(element, deferreds, request, { answerIsCart = true } = {}) {
  let result;
  try {
    const { ok, json } = await request();
    const cart = ok && answerIsCart ? json : await getCart();
    result = {
      cart,
      sections: ok ? json.sections : undefined,
      items: ok && !answerIsCart ? json.items : undefined,
      userErrors: ok ? [] : [{ message: json.description || json.message || '' }]
    };
  } catch (error) {
    element.dispatchEvent(new CartErrorEvent({ error: error.message || 'Cart request failed', code: 'SERVICE_UNAVAILABLE' }));
    deferreds.forEach((d) => d.reject(error));
    throw error;
  }
  const settled = {
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result.cart),
    detail: { sections: result.sections, items: result.items }
  };
  if (result.userErrors.length) settled.userErrors = result.userErrors;
  /* One object for every event of this request, so a listener that hears
     two (a discount and the lines it removed) can tell it is one change. */
  deferreds.forEach((d) => d.resolve(settled));
  if (result.userErrors.length) throw new CartDeclinedError(result.userErrors[0].message);
  return result;
}

/**
 * Adds lines: [{ id: variantId, quantity, properties }]. Sent as Shopify's
 * `items` array, the format it checks stock on (a single {id, quantity}
 * adds a sold-out variant with a 200; verified 2026-10-06).
 * context: 'product' (a product form) or 'dialog' (quick add, the gift).
 */
export function addToCart(lines, { element = document, context = 'product' } = {}) {
  const deferred = CartLinesUpdateEvent.createPromise();
  element.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context,
    lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity || 1 })),
    promise: deferred.promise
  }));
  return run(element, [deferred], () => post(window.routes.cart_add_url + '.js', { items: lines }), { answerIsCart: false });
}

/**
 * Sets one line's quantity (0 removes it). `line` is the 1-based line
 * index, as Dawn and Horizon send it: a line's key changes when its
 * discounts change, its place does not. `key` names the line in the event.
 */
export function changeLine(line, quantity, { element = document, key = '' } = {}) {
  const deferred = CartLinesUpdateEvent.createPromise();
  element.dispatchEvent(new CartLinesUpdateEvent({
    action: quantity === 0 ? 'remove' : 'update',
    context: 'cart',
    lines: [{ id: String(key), quantity }],
    promise: deferred.promise
  }));
  return run(element, [deferred], () => post(window.routes.cart_change_url + '.js', { line, quantity }));
}

/**
 * Sets the cart's whole set of discount codes and, in the same request,
 * line quantities keyed by line key ({ [key]: 0 } removes a line).
 */
export function updateCart({ discountCodes, updates } = {}, { element = document, context = 'cart' } = {}) {
  const body = {};
  const deferreds = [];
  if (discountCodes) {
    body.discount = discountCodes.join(',');
    const deferred = CartDiscountUpdateEvent.createPromise();
    deferreds.push(deferred);
    element.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: discountCodes.map((code) => ({ code })), promise: deferred.promise }));
  }
  if (updates && Object.keys(updates).length) {
    body.updates = updates;
    const quantities = Object.values(updates);
    const deferred = CartLinesUpdateEvent.createPromise();
    deferreds.push(deferred);
    element.dispatchEvent(new CartLinesUpdateEvent({
      action: quantities.every((q) => q === 0) ? 'remove' : 'update',
      context,
      lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
      promise: deferred.promise
    }));
  }
  return run(element, deferreds, () => post(window.routes.cart_update_url + '.js', body));
}

/** Adds one code to the codes already on the cart (Shopify replaces the set). */
export async function applyDiscountCode(code, options) {
  const cart = await getCart();
  const codes = (cart.discount_codes || []).map((c) => c.code).filter((c) => c.toUpperCase() !== code.toUpperCase());
  return updateCart({ discountCodes: codes.concat([code]) }, options);
}

/* The Storefront API version discountCodeProblem() is written against. */
const STOREFRONT_API_VERSION = '2026-10';

/**
 * Why the cart does not apply `code`: Shopify's reason as a Storefront API
 * cart warning code ('DISCOUNT_NOT_FOUND', 'DISCOUNT_CURRENTLY_INACTIVE',
 * 'DISCOUNT_CODE_NOT_HONOURED', …;
 * shopify.dev/docs/api/storefront/latest/enums/CartWarningCode), or '' when
 * it gives none, the reason could be another code's, or the request fails.
 *
 * The Cart AJAX API answers applicable:false and nothing more, for a code
 * that does not exist, one that has ended and one this cart does not
 * qualify for alike. The Storefront API's cartDiscountCodesUpdate says
 * which, as a warning. `cart` is the AJAX cart that was just given `codes`
 * (its token is its Storefront id), so asking with the same codes changes
 * nothing. No access token: carts are open to tokenless access
 * (shopify.dev/docs/api/storefront#authentication).
 */
export async function discountCodeProblem(cart, codes, code) {
  try {
    const response = await fetch('/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: 'mutation($id: ID!, $codes: [String!]!) { cartDiscountCodesUpdate(cartId: $id, discountCodes: $codes) { cart { discountCodes { code applicable } } warnings { code } } }',
        variables: { id: 'gid://shopify/Cart/' + cart.token, codes }
      })
    });
    const json = await response.json();
    const answer = (json.data && json.data.cartDiscountCodesUpdate) || {};
    /* A warning does not name its code. It is this code's only when this is
       the one code that does not apply and there is one reason. */
    const refused = ((answer.cart && answer.cart.discountCodes) || []).filter((c) => !c.applicable);
    const reasons = (answer.warnings || []).filter((w) => /^DISCOUNT_/.test(w.code));
    const mine = refused.length === 1 && refused[0].code.toUpperCase() === code.toUpperCase();
    return mine && reasons.length === 1 ? reasons[0].code : '';
  } catch (error) {
    return '';
  }
}

/* The cart events whose result carries the cart. */
const CART_EVENTS = ['shopify:cart:lines-update', 'shopify:cart:discount-update', 'shopify:cart:note-update', 'shopify:cart:attributes-update'];

/**
 * Calls back with each cart change's result once it is in, whoever made it
 * (this module, an app through Shopify.actions.updateCart). A request that
 * dispatched two events calls back once.
 */
export function onCartUpdate(callback, { signal } = {}) {
  const seen = new WeakSet();
  const handler = (event) => {
    if (!event.promise) return;
    event.promise.then((result) => {
      if (!result || seen.has(result)) return;
      seen.add(result);
      callback(result, event);
    }, () => { /* failed: shopify:cart:error says so */ });
  };
  CART_EVENTS.forEach((name) => document.addEventListener(name, handler, { signal }));
}

/* Batched section loads, for a change that came without sections (an app's
   updateCart). Every id asked for in the same moment goes out as ONE
   request, ?sections=a,b. A request already on its way is never reused: it
   may predate the change. */
let batch = null;
export function loadSection(id) {
  if (!batch) {
    const b = (batch = { ids: [] });
    b.promise = new Promise((resolve) => {
      queueMicrotask(() => {
        batch = null;
        const url = window.location.pathname + '?sections=' + b.ids.map(encodeURIComponent).join(',');
        resolve(fetch(url, { cache: 'no-store' }).then((r) => r.json()));
      });
    });
  }
  if (!batch.ids.includes(id)) batch.ids.push(id);
  return batch.promise.then((data) => (data ? data[id] : undefined));
}

/** Opens the cart drawer; the cart page when there is none (on /cart itself). */
export function openCart(trigger) {
  const drawer = document.querySelector('cba-cart-drawer');
  if (drawer && typeof drawer.open === 'function') drawer.open(trigger);
  else window.location.href = window.routes.cart_url;
}

/* Shopify.actions: an app's updateCart emits its events from the cart
   drawer, and openCart opens it (Horizon assets/standard-actions-override.js).
   Shopify adds the actions as a module at the end of <body>, so they are
   there by DOMContentLoaded. */
function configureActions() {
  const actions = window.Shopify && window.Shopify.actions;
  if (!actions) return;
  if (actions.updateCart) actions.updateCart.configure({ eventTarget: () => document.querySelector('cba-cart-drawer') || document });
  if (actions.openCart) actions.openCart.configure({ handler: async () => openCart() });
}
if (window.Shopify && window.Shopify.actions) configureActions();
else document.addEventListener('DOMContentLoaded', configureActions, { once: true });
